import { createPortal } from 'react-dom';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useModal } from '../../context/ModalProvider';
import {
  SCHEDULE_MEETING_TYPE_COLORS,
  SCHEDULE_MEETING_TYPE_LABELS,
  type ScheduleMeetingType,
} from '../../lib/schedule/meetingTypes';

type RecurringPattern = 'weekly' | 'bi-weekly' | 'monthly';

interface RecurringScheduleModalProps {
  onCreateRecurring: (config: {
    scheduleType: ScheduleMeetingType;
    pattern: RecurringPattern;
    daysOfWeek: number[];
    selectedTimes: string[];
    startDate: Date;
    endDate: Date | null;
    name: string;
  }) => void;
  availableTypes: ScheduleMeetingType[];
  timeSlots: string[];
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const Panel = styled.div`
  width: min(760px, 100%);
  max-height: 90vh;
  overflow: auto;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: ${({ theme }) => theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray700};
  padding: 1.2rem;
  display: grid;
  gap: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.heading};
`;

const Subtitle = styled.p`
  margin: 0.3rem 0 0;
  color: ${({ theme }) => theme.colors.gray700};
  opacity: 0.85;
  font-size: 0.9rem;
`;

const CloseButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: transparent;
  color: ${({ theme }) => theme.colors.gray700};
  border-radius: 999px;
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const Section = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-size: 0.95rem;
`;

const Row = styled.div`
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const Label = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 700;
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.gray700};
  border-radius: 10px;
  min-height: 38px;
  padding: 0 10px;
`;

const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.gray700};
  border-radius: 10px;
  min-height: 38px;
  padding: 0 10px;
`;

const TypeTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TypeTab = styled.button<{ $active: boolean; $type: ScheduleMeetingType }>`
  border: 1px solid;
  border-radius: 999px;
  min-height: 34px;
  padding: 0 10px;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  ${({ $active, $type, theme }) => {
    const color = SCHEDULE_MEETING_TYPE_COLORS[$type];
    if ($active) {
      return `
        background: ${color};
        border-color: ${color};
        color: #fff;
      `;
    }
    return `
      background: ${theme.colors.gray100};
      border-color: ${theme.colors.gray200};
      color: ${theme.colors.gray700};
    `;
  }}
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.4rem;
`;

const DayButton = styled.button<{ $on: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 9px;
  min-height: 34px;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
  ${({ $on, theme }) =>
    $on
      ? `
    background: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: #fff;
  `
      : `
    background: ${theme.colors.gray100};
    color: ${theme.colors.gray700};
  `}
`;

const TimeGrid = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 11px;
  padding: 0.6rem;
  background: ${({ theme }) => theme.colors.gray100};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 0.45rem;
  max-height: 190px;
  overflow: auto;
`;

const TimeButton = styled.button<{ $on: boolean; $type: ScheduleMeetingType }>`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 8px;
  min-height: 31px;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  ${({ $on, $type, theme }) => {
    const color = SCHEDULE_MEETING_TYPE_COLORS[$type];
    if ($on) {
      return `
        background: ${color};
        border-color: ${color};
        color: #fff;
      `;
    }
    return `
      background: ${theme.colors.gray050};
      color: ${theme.colors.gray700};
    `;
  }}
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  padding-top: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const FooterInfo = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray700};
  opacity: 0.85;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
  border: 1px solid;
  border-radius: 10px;
  min-height: 38px;
  padding: 0 14px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  ${({ $primary, theme }) =>
    $primary
      ? `
    background: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: #fff;
  `
      : `
    background: transparent;
    border-color: ${theme.colors.gray200};
    color: ${theme.colors.gray700};
  `}
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function RecurringScheduleModal({
  onCreateRecurring,
  availableTypes,
  timeSlots,
}: RecurringScheduleModalProps) {
  const { close } = useModal();
  const [scheduleType, setScheduleType] = useState<ScheduleMeetingType>(
    availableTypes[0] ?? 'general_meeting',
  );
  const [pattern, setPattern] = useState<RecurringPattern>('weekly');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);

  const summaryText = useMemo(() => {
    const previewName =
      name.trim() || `${SCHEDULE_MEETING_TYPE_LABELS[scheduleType]} recurring`;
    return `${previewName} | ${selectedTimes.length} time slot(s)`;
  }, [name, scheduleType, selectedTimes.length]);

  const toggleDay = (dayIndex: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(dayIndex)
        ? prev.filter((d) => d !== dayIndex)
        : [...prev, dayIndex].sort((a, b) => a - b),
    );
  };

  const toggleTime = (time: string) => {
    setSelectedTimes((prev) =>
      prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time],
    );
  };

  const isValid = daysOfWeek.length > 0 && selectedTimes.length > 0 && startDate;
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleSubmit = () => {
    if (!isValid) return;
    onCreateRecurring({
      scheduleType,
      pattern,
      daysOfWeek,
      selectedTimes,
      startDate: new Date(`${startDate}T00:00:00`),
      endDate: endDate ? new Date(`${endDate}T00:00:00`) : null,
      name: name.trim() || `${SCHEDULE_MEETING_TYPE_LABELS[scheduleType]} recurring`,
    });
    close();
  };

  return createPortal(
    <Backdrop onClick={close}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Header>
          <div>
            <Title>Create recurring availability</Title>
            <Subtitle>Set weekly, bi-weekly, or monthly recurring slots.</Subtitle>
          </div>
          <CloseButton onClick={close} aria-label="Close recurring schedule modal">
            x
          </CloseButton>
        </Header>

        <Section>
          <SectionTitle>Meeting type</SectionTitle>
          <TypeTabs>
            {availableTypes.map((type) => (
              <TypeTab
                key={type}
                $active={scheduleType === type}
                $type={type}
                onClick={() => setScheduleType(type)}
                type="button"
              >
                {SCHEDULE_MEETING_TYPE_LABELS[type]}
              </TypeTab>
            ))}
          </TypeTabs>
        </Section>

        <Section>
          <SectionTitle>Rule settings</SectionTitle>
          <Row>
            <Label>
              Rule name (optional)
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Example: Weekly sync"
              />
            </Label>
            <Label>
              Pattern
              <Select
                value={pattern}
                onChange={(event) =>
                  setPattern(event.target.value as RecurringPattern)
                }
              >
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </Label>
            <Label>
              Start date
              <Input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Label>
            <Label>
              End date (optional)
              <Input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                min={startDate}
              />
            </Label>
          </Row>
        </Section>

        <Section>
          <SectionTitle>Days of week</SectionTitle>
          <DayGrid>
            {dayLabels.map((label, index) => (
              <DayButton
                key={label}
                $on={daysOfWeek.includes(index)}
                onClick={() => toggleDay(index)}
                type="button"
              >
                {label}
              </DayButton>
            ))}
          </DayGrid>
        </Section>

        <Section>
          <SectionTitle>Time slots</SectionTitle>
          <TimeGrid>
            {timeSlots.map((time) => (
              <TimeButton
                key={time}
                $on={selectedTimes.includes(time)}
                $type={scheduleType}
                onClick={() => toggleTime(time)}
                type="button"
              >
                {time}
              </TimeButton>
            ))}
          </TimeGrid>
        </Section>

        <Footer>
          <FooterInfo>{summaryText}</FooterInfo>
          <ButtonRow>
            <Button type="button" onClick={close}>
              Cancel
            </Button>
            <Button type="button" $primary onClick={handleSubmit} disabled={!isValid}>
              Create rule
            </Button>
          </ButtonRow>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body,
  );
}
